import {
  Avatar,
  ButtonList,
  DropdownButton,
  FlexBox,
  Spinner,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { accessControl } from 'lib-shared-access-control';
import { FullPresenceT, LoadDescriptionT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import React, { ReactNode, useCallback, useMemo } from 'react';

import { AwayStatus, PresenceAgentV2, WorkloadLimits } from 'apis/presence';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';

import { LoadingDropdownItem } from '../loadingDropdownItem';
import { TeamCard } from '../teamCard';
import { TeamChannelIconsList } from '../teamChannelIconsList';
import { TeamConversation } from '../teamConversation/TeamConversation';
import type { TeamListProps } from '../teamList/teamList';

type PropsT = {
  userPresence: PresenceAgentV2;
  isOwnAgent: boolean;
  profileId: string;
  isFavorite: boolean;
  isOpen: boolean;
  limits: WorkloadLimits;
  onFavoriteClick: () => void;
  toggleAgentDetails: (id: string) => void;
  awayStatuses: AwayStatus[];
} & Pick<
  TeamListProps,
  | 'appPresence'
  | 'showUserModal'
  | 'isMicrophoneBlocked'
  | 'twilioMakeInternalCall'
  | 'toggleAgentDetails'
  | 'transferToQueueModalOpen'
  | 'getPresence'
>;

export const TeamTableRow = ({
  userPresence,
  isOwnAgent,
  profileId,
  isFavorite,
  isOpen,
  limits,
  onFavoriteClick,
  toggleAgentDetails,
  awayStatuses,
  appPresence,
  showUserModal,
  isMicrophoneBlocked,
  twilioMakeInternalCall,
  transferToQueueModalOpen,
  getPresence,
}: PropsT): JSX.Element => {
  const findFullPresence = useCallback((): FullPresenceT | undefined => {
    const tail = appPresence.items[appPresence.items.length - 1];
    if (tail?.user.id === userPresence.agent_id) {
      return tail;
    }
    return appPresence.items.find((x) => x.user.id === userPresence.agent_id);
  }, [appPresence.items, userPresence.agent_id]);

  const fullPresence = useMemo(() => findFullPresence(), [findFullPresence]);

  const loadDescriptions = useCallback((): LoadDescriptionT[] => {
    return (
      fullPresence?.presence.presence_status.load_descriptions.filter(
        (ld) => ld.conversation,
      ) || []
    );
  }, [fullPresence]);

  const getPresenceStatus = useCallback((): string => {
    return userPresence.connection_status === 'offline'
      ? 'offline'
      : userPresence.status;
  }, [userPresence]);

  const presenceStatus = useMemo(
    () => getPresenceStatus(),
    [getPresenceStatus],
  );

  const getChannelCapabilities = useCallback(() => {
    const { non_working_channels } = userPresence;
    const foundSpeakLoad = loadDescriptions().filter((load) => {
      const { conversation_channel: channel, load_type: loadType } = load;
      return (
        ['pstnphone', 'callback'].includes(channel) &&
        !['acw', 'nonload'].includes(loadType)
      );
    }).length;

    const canMakeInternalCall = !(
      foundSpeakLoad > 0 ||
      isMicrophoneBlocked ||
      isOwnAgent ||
      presenceStatus === 'offline' ||
      non_working_channels.some((channel: string) => channel === 'pstnphone')
    );

    return {
      isSimultaneousRingingActive: userPresence.simultaneous_ring,
      canMakeInternalCall,
    };
  }, [
    userPresence,
    isMicrophoneBlocked,
    isOwnAgent,
    presenceStatus,
    loadDescriptions,
  ]);

  const capabilities = useMemo(
    () => getChannelCapabilities(),
    [getChannelCapabilities],
  );

  const teamToggleAgentCardClickHandler = useCallback(
    (e: React.MouseEvent<HTMLElement>, open: boolean) => {
      // When clicking on a dropdown button we want to expand the dropdown but not the accordion
      const clickedOnDropdownButton = !!(e.target as HTMLElement).closest(
        '[data-dropdown-button="true"]',
      );
      if (clickedOnDropdownButton) {
        return;
      }

      if (!fullPresence) {
        getPresence({
          isOwnAgent,
          user_id: userPresence.agent_id,
        });
      }
      open ? ampli.teamAgentExpanded() : ampli.teamAgentCollapsed();
      toggleAgentDetails(userPresence.agent_id);
    },
    [
      fullPresence,
      getPresence,
      isOwnAgent,
      userPresence.agent_id,
      toggleAgentDetails,
    ],
  );

  const handleTwilioMakeInternalCall = useCallback(() => {
    twilioMakeInternalCall({ userId: userPresence.agent_id });
  }, [twilioMakeInternalCall, userPresence.agent_id]);

  const handleShowUserModal = useCallback(() => {
    if (fullPresence?.user) {
      showUserModal(fullPresence.user);
    } else {
      console.warn('No user in presence defined');
    }
  }, [fullPresence, showUserModal]);

  const onActionsDropdownClick = useCallback(() => {
    if (!fullPresence) {
      getPresence({
        isOwnAgent,
        user_id: userPresence.agent_id,
      });
    }
  }, [fullPresence, getPresence, isOwnAgent, userPresence.agent_id]);

  const onEditClick = useCallback(() => {
    handleShowUserModal();
    ampli.teamAgentActionsMenuItemClicked({ type: 'edit' });
  }, [handleShowUserModal]);

  const onCallClick = useCallback(() => {
    handleTwilioMakeInternalCall();
    ampli.teamAgentActionsMenuItemClicked({ type: 'call' });
  }, [handleTwilioMakeInternalCall]);

  const getAgentName = useCallback((): ReactNode => {
    const displayName = (userPresence.name || '').trim();
    return `${displayName} ${isOwnAgent ? '(you)' : ''}`;
  }, [userPresence.name, isOwnAgent]);

  const getAvatarWithFullNameTooltip = useCallback(
    (presence?: FullPresenceT): ReactNode => {
      const displayName = (userPresence.name || '').trim();
      const agentName = `${displayName} ${isOwnAgent ? '(you)' : ''}`;
      const { user: { given_name = '', family_name = '' } = {} } =
        presence || {};
      const fullName = `${given_name || ''} ${family_name || ''}`.trim();
      const name = fullName || displayName;

      return (
        <Tooltip content={name}>
          <Avatar
            user={{
              id: userPresence.agent_id,
              name: agentName,
              avatar_url: userPresence.avatar_url,
            }}
          />
        </Tooltip>
      );
    },
    [
      userPresence.agent_id,
      userPresence.name,
      userPresence.avatar_url,
      isOwnAgent,
    ],
  );

  const getCustomAwayStatus = useCallback(
    (status: string, presence?: FullPresenceT): ReactNode => {
      if (status !== 'away') return null;
      const {
        custom_away_status_id: customAwayStatusId,
        status_updated_at: statusUpdatedAt,
      } = presence?.presence?.presence_status ?? {};

      if (!customAwayStatusId) return null;

      const customAwayStatus = awayStatuses.find(
        (s) => s.id === customAwayStatusId,
      );
      if (!customAwayStatus) return null;

      return (
        <CustomAwayStatus
          awayStatus={customAwayStatus}
          statusUpdatedAt={statusUpdatedAt || null}
          showLabel={true}
        />
      );
    },
    [awayStatuses],
  );

  const hasConversationReplyAccess =
    accessControl.hasAccess('conversation.reply');
  const hasOrganizationMemberAccess = accessControl.hasAccess(
    'organization.member',
  );

  const conversationList = useCallback(
    (conversationRequester: LoadDescriptionT) => {
      const { csid, conversation, requester, conversation_channel, load_type } =
        conversationRequester;
      return (
        <TeamConversation
          key={csid}
          channel={conversation_channel}
          loadType={load_type}
          csid={csid}
          profileId={profileId}
          conversation={conversation}
          user={fullPresence?.user ?? null}
          requester={requester}
          transferToQueueModalOpen={transferToQueueModalOpen}
        />
      );
    },
    [profileId, fullPresence, transferToQueueModalOpen],
  );

  return (
    <Table.Tr>
      <Table.Td style={{ padding: 0 }}>
        <TeamCard isOpen={isOpen}>
          <TeamCard.Header
            iconsList={
              <TeamCard.Icons>
                <TeamChannelIconsList
                  workloads_by_channel={userPresence.workloads_by_channel}
                  fullPresence={fullPresence}
                  limits={limits}
                  isSimultaneousRingingActive={
                    capabilities.isSimultaneousRingingActive
                  }
                />
              </TeamCard.Icons>
            }
            actions={
              isOwnAgent ||
              hasOrganizationMemberAccess ||
              hasConversationReplyAccess ? (
                <div onClick={onActionsDropdownClick}>
                  <DropdownButton
                    data-dropdown-button={true}
                    icon="dots-h"
                    noArrow
                    buttonProps={{ variant: 'tertiary' }}
                    onToggle={(open) =>
                      open && ampli.teamAgentActionsMenuOpened()
                    }
                    dropdown={
                      appPresence.asyncStatus === 'loading' ? (
                        <LoadingDropdownItem />
                      ) : (
                        <ButtonList>
                          {hasConversationReplyAccess &&
                            capabilities.canMakeInternalCall && (
                              <ButtonList.Item
                                onClick={onCallClick}
                                icon="phone"
                              >
                                Call
                              </ButtonList.Item>
                            )}
                          {(isOwnAgent || hasOrganizationMemberAccess) && (
                            <ButtonList.Item onClick={onEditClick} icon="pen">
                              Edit
                            </ButtonList.Item>
                          )}
                        </ButtonList>
                      )
                    }
                  />
                </div>
              ) : undefined
            }
            status={presenceStatus}
            avatar={getAvatarWithFullNameTooltip(fullPresence)}
            isFavorite={isFavorite}
            onFavoriteClick={onFavoriteClick}
            isOpen={isOpen}
            onToggle={teamToggleAgentCardClickHandler}
          >
            <FlexBox gap="300" alignItems="center">
              {getAgentName()}
              {getCustomAwayStatus(presenceStatus, fullPresence)}
            </FlexBox>
          </TeamCard.Header>
          <TeamCard.Content>
            {loadDescriptions().length === 0 && (
              <FlexBox justifyContent="center" alignItems="center">
                {appPresence.asyncStatus === 'loading' && !fullPresence ? (
                  <Spinner />
                ) : (
                  'No conversations found'
                )}
              </FlexBox>
            )}
            {isOpen && loadDescriptions().map(conversationList)}
          </TeamCard.Content>
        </TeamCard>
      </Table.Td>
    </Table.Tr>
  );
};
